'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "resourceId", "addPropertiesToDroppedEvent", "children"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from '@mui/x-scheduler-internals/base-ui-copy';
import { schedulerOccurrenceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventOccurrencesWithTimelinePosition } from '@mui/x-scheduler-internals/use-event-occurrences-with-timeline-position';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { useEventCreation, useKeyboardEventCreation } from '@mui/x-scheduler-internals/internals';
import { EVENT_CREATION_PRECISION_MINUTE } from '@mui/x-scheduler-internals/constants';
import { TimelineGridEventRowContext } from "./TimelineGridEventRowContext.mjs";
import { useEventRowDropTarget } from "./useEventRowDropTarget.mjs";
import { usePlaceholderInRow } from "./usePlaceholderInRow.mjs";
import { useEventTimelinePremiumStoreContext } from "../../use-event-timeline-premium-store-context/index.mjs";
import { eventTimelinePremiumPresetSelectors } from "../../event-timeline-premium-selectors/index.mjs";
import { TimelineGridEventRowDataAttributes } from "./TimelineGridEventRowDataAttributes.mjs";
import { useTimelineGridRowKeyboard } from "../../internals/utils/useTimelineGridRowKeyboard.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const stateAttributesMapping = {
  resourceId: value => ({
    [TimelineGridEventRowDataAttributes.resourceId]: String(value)
  }),
  creationDisabled: value => value ? {
    [TimelineGridEventRowDataAttributes.creationDisabled]: ''
  } : null
};
export const TimelineGridEventRow = /*#__PURE__*/React.forwardRef(function TimelineGridEventRow(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      resourceId,
      addPropertiesToDroppedEvent,
      children: childrenProp
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    rowRef,
    hasFocus,
    handleKeyDown,
    handleFocus
  } = useTimelineGridRowKeyboard({
    columnType: 'events'
  });

  // Selector hooks
  const presetConfig = useStore(store, eventTimelinePremiumPresetSelectors.config);
  const occurrences = useStore(store, schedulerOccurrenceSelectors.resourceOccurrences, presetConfig.start, presetConfig.end, resourceId);

  // Feature hooks
  const {
    getCursorPositionInElementMs,
    ref: dropTargetRef
  } = useEventRowDropTarget({
    resourceId,
    addPropertiesToDroppedEvent
  });
  const eventCreationProps = useEventCreation(({
    event,
    creationConfig
  }) => {
    const offsetMs = getCursorPositionInElementMs({
      input: {
        clientX: event.clientX
      },
      elementRef: dropTargetRef
    });
    const anchor = adapter.addMilliseconds(presetConfig.start, offsetMs);
    const startDate = adapter.addMinutes(anchor, -(adapter.getMinutes(anchor) % EVENT_CREATION_PRECISION_MINUTE));
    return {
      surfaceType: 'timeline',
      start: startDate,
      end: adapter.addMinutes(startDate, creationConfig.duration),
      resourceId,
      lockSurfaceType: true
    };
  });
  const triggerKeyboardCreation = useKeyboardEventCreation(({
    creationConfig
  }) => ({
    surfaceType: 'timeline',
    start: presetConfig.start,
    end: adapter.addMinutes(presetConfig.start, creationConfig.duration),
    resourceId,
    lockSurfaceType: true
  }));
  const onKeyDown = event => {
    if (handleKeyDown(event)) {
      return;
    }
    if (event.key === 'Enter' && event.target === event.currentTarget && triggerKeyboardCreation) {
      event.preventDefault();
      triggerKeyboardCreation();
    }
  };
  const contextValue = React.useMemo(() => ({
    hasFocus,
    getCursorPositionInElementMs
  }), [hasFocus, getCursorPositionInElementMs]);
  const occurrencesWithPosition = useEventOccurrencesWithTimelinePosition({
    occurrences,
    maxSpan: 1
  });
  const placeholder = usePlaceholderInRow({
    resourceId,
    occurrences: occurrencesWithPosition.occurrences,
    maxIndex: occurrencesWithPosition.maxIndex
  });
  const children = React.useMemo(() => childrenProp(_extends({
    placeholder
  }, occurrencesWithPosition)), [childrenProp, placeholder, occurrencesWithPosition]);
  const state = {
    resourceId,
    creationDisabled: !triggerKeyboardCreation
  };
  const keyboardProps = {
    tabIndex: 0,
    onKeyDown,
    onFocus: handleFocus
  };
  const element = useRenderElement('div', componentProps, {
    ref: [forwardedRef, dropTargetRef, rowRef],
    state,
    stateAttributesMapping,
    props: [elementProps, {
      children,
      style: {
        '--lane-count': occurrencesWithPosition.maxIndex
      }
    }, keyboardProps, eventCreationProps]
  });
  return /*#__PURE__*/_jsx(TimelineGridEventRowContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") TimelineGridEventRow.displayName = "TimelineGridEventRow";